import * as React from 'react';
import { isSupabase } from '@/lib/supabase';
import { 
  getLessonWithMedia, 
  getMainVideoForLesson, 
  getSupportingMediaForLesson, 
  getMediaMappingsByLesson,
  getMainVideoUrl,
  type MediaMapping,
  type LessonWithMedia 
} from '@/repos/contentRepo';
import type { LessonLite } from '@/types/content';
import { validateMediaMapping } from '@/utils/providerDetector';

// Hook para buscar lição com todas as mídias
export function useLessonWithMedia(lessonId?: string) {
  const [lessonWithMedia, setLessonWithMedia] = React.useState<LessonWithMedia | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!lessonId) return;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!isSupabase()) {
          console.log('⚠️ Não é Supabase, usando dados mock');
          // Fallback para dados mock se não for Supabase
          setLessonWithMedia({
            id: lessonId,
            module_id: 1,
            title: 'Lição de Exemplo',
            description: 'Lição de exemplo',
            video_url: '',
            provider: 'native',
            order: 1,
            duration: '30 min',
            is_free: true,
            mainVideo: undefined,
            supportingMedia: [],
            allMedia: []
          });
          return;
        }
        
        const lesson = await getLessonWithMedia(lessonId);
        setLessonWithMedia(lesson);
        
      } catch (e: any) {
        console.error('❌ Erro em useLessonWithMedia:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [lessonId]);

  return { lessonWithMedia, loading, error };
}

// Hook para buscar apenas o vídeo principal
export function useMainVideo(lessonId?: string) {
  const [mainVideo, setMainVideo] = React.useState<MediaMapping | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!lessonId) return;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!isSupabase()) {
          console.log('⚠️ Não é Supabase, retornando null para vídeo principal');
          setMainVideo(null);
          return;
        }
        
        const video = await getMainVideoForLesson(lessonId);
        
        // Validar dados do vídeo
        if (video) {
          const validation = validateMediaMapping(video);
          if (!validation.isValid) {
            console.warn('⚠️ useMainVideo: Dados do vídeo inválidos:', validation.errors);
          }
        }
        
        setMainVideo(video);
        
      } catch (e: any) {
        console.error('❌ Erro em useMainVideo:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [lessonId]);

  return { mainVideo, loading, error };
}

// Hook para buscar mídias de apoio
export function useSupportingMedia(lessonId?: string) {
  const [supportingMedia, setSupportingMedia] = React.useState<MediaMapping[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!lessonId) return;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!isSupabase()) {
          console.log('⚠️ Não é Supabase, retornando array vazio para mídias de apoio');
          setSupportingMedia([]);
          return;
        }
        
        const media = await getSupportingMediaForLesson(lessonId);
        setSupportingMedia(media);
        
      } catch (e: any) {
        console.error('❌ Erro em useSupportingMedia:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [lessonId]);

  return { supportingMedia, loading, error };
}

// Hook para buscar todas as mídias de uma lição
export function useLessonMedia(lessonId?: string) {
  const [mediaMappings, setMediaMappings] = React.useState<MediaMapping[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!lessonId) return;
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!isSupabase()) {
          console.log('⚠️ Não é Supabase, retornando array vazio para mídias');
          setMediaMappings([]);
          return;
        }
        
        const media = await getMediaMappingsByLesson(lessonId);
        setMediaMappings(media);
        
      } catch (e: any) {
        console.error('❌ Erro em useLessonMedia:', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [lessonId]);

  return { mediaMappings, loading, error };
}

// Hook para obter URL do vídeo principal com fallback
export function useMainVideoUrl(lesson: LessonLite | null, mediaMappings: MediaMapping[]) {
  return React.useMemo(() => {
    if (!lesson) {
      return { url: '', provider: 'native', config: null };
    }
    
    return getMainVideoUrl(lesson, mediaMappings);
  }, [lesson, mediaMappings]);
}

// Hook combinado para facilitar o uso
export function useLessonMediaComplete(lessonId?: string) {
  const { lessonWithMedia, loading: lessonLoading, error: lessonError } = useLessonWithMedia(lessonId);
  const { mainVideo, loading: mainVideoLoading, error: mainVideoError } = useMainVideo(lessonId);
  const { supportingMedia, loading: supportingLoading, error: supportingError } = useSupportingMedia(lessonId);
  const { mediaMappings, loading: mediaLoading, error: mediaError } = useLessonMedia(lessonId);

  const loading = lessonLoading || mainVideoLoading || supportingLoading || mediaLoading;
  const error = lessonError || mainVideoError || supportingError || mediaError;

  // Obter URL do vídeo principal com fallback
  const mainVideoUrl = useMainVideoUrl(lessonWithMedia, mediaMappings);

  return {
    lesson: lessonWithMedia,
    mainVideo,
    supportingMedia,
    allMedia: mediaMappings,
    mainVideoUrl,
    loading,
    error
  };
}
